import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import styled from '@emotion/styled';
import { Box, Fade, Menu, MenuItem, popoverClasses, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { Button } from '@components/index';
import { GlobalLoader } from '@components/loader';
import { SessionOut } from '@components/sessionOutDialog/SessionOut';
import { RoutingTabs } from '@components/tab';
import { Toast } from '@components/toast';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { CheckIcon, GlobeIcon } from '@icons/index';
import localStorage from '@repositories/browserStorage/localStorage/LocalStorage';
import { useGetCategories } from '@queryHooks/useCategory';
import { useProviderIdByDomain } from '@queryHooks/useProvider';
import { getDomainFromBaseUrl } from '@utils/urlUtils';
import i18n from '@locales/i18n';
import { Modals } from '../../modals';
import { ProviderStoreProvider } from './ProviderStoreContext';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

/**
 * 판매자 전용 스토어 레이아웃
 * - GNB에 소개, 스토어 탭만 표시
 * - 로그인, MY 구독, 개인정보 처리방침 표시 안 함
 */
export const ProviderStoreLayout = observer(() => {
  const { authStore, categoryStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { providerDomain } = useParams<{ providerDomain: string }>();
  const providerId = useProviderIdByDomain(providerDomain);

  useLayoutEffect(() => {
    authStore.initialize();
  }, [authStore]);

  const handleCloseSessionOut = () => {
    navigate('/login', { state: { routeAfterLogin: location.pathname } });
  };

  const { data: categories } = useGetCategories(
    { providerId },
    { enabled: !!providerId },
  );

  useEffect(() => {
    if (categories) {
      categoryStore.setCategories(categories.content);
    }
  }, [categories, categoryStore]);

  return (
    <ProviderStoreProvider value={{ providerId }}>
      <div className="app customer">
        <ProviderStoreGnb />
        <Main>
          <ContentsWrapper>
            <Outlet />
          </ContentsWrapper>
          <SessionOut onClose={handleCloseSessionOut} />
        </Main>
        <Modals />
        <GlobalLoader />
        <Toast />
      </div>
    </ProviderStoreProvider>
  );
});

const ProviderStoreGnb = observer(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { providerDomain } = useParams<{ providerDomain: string }>();
  const { INTRODUCTION_PAGE, COMPANY_ID } = getEnvConfig();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [langAnchorEl, setLangAnchorEl] = useState<HTMLButtonElement | null>(null);

  const providerStorePath = `/stores/${providerDomain}`;

  // 소개, 스토어 탭만 표시 (MY 구독 제외)
  const tabList =
    INTRODUCTION_PAGE === 'true'
      ? [
          { name: t('GNB_Menu_01'), path: `${providerStorePath}/introduction` },
          { name: t('Ad_GNB_Menu_01'), path: providerStorePath },
        ]
      : [{ name: t('Ad_GNB_Menu_01'), path: providerStorePath }];

  const handleIntroduceInquiryClick = useCallback(() => {
    navigate(`${providerStorePath}/tenant-request`);
  }, [navigate, providerStorePath]);

  const handleChangeLang = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
    dayjs.locale(lang);
    localStorage.set('lang', lang);

    const isSecure = window.location.protocol === 'https:';
    const secureAttr = isSecure ? ' secure;' : '';
    const domainAttr = getDomainFromBaseUrl();
    const cookieString = `SMP_lang=${lang}; path=/;${secureAttr} ${domainAttr}`;
    document.cookie = cookieString;

    setIsLangMenuOpen(false);
  }, []);

  const handleLangButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setLangAnchorEl(event.currentTarget);
    setIsLangMenuOpen(prev => !prev);
  }, []);

  const handleLangMenuClose = useCallback(() => {
    setIsLangMenuOpen(false);
  }, []);

  return (
    <GnbContainer>
      {matches ? (
        <ProviderStoreMobileGnb />
      ) : (
        <>
          <LogoLinkWrap to={providerStorePath}>
            <img
              src={COMPANY_ID === 'KT_DS' ? '/logo-ktds.png' : '/logo.png'}
              alt="Logo"
              style={{ width: '100%', height: '100%', objectFit: 'scale-down' }}
            />
          </LogoLinkWrap>
          <MenusWrapper>
            <StyledRoutingTabs variant="line" tabList={tabList} />
          </MenusWrapper>
          <RightSection>
            <IntroductionBtn onClick={handleIntroduceInquiryClick} variant="contain" size="small" paddingHorizontal={16}>
              {t('GNB_Menu_04')}
            </IntroductionBtn>
            <LangButton onClick={handleLangButtonClick}>
              <GlobeIcon width={18} height={18} />
            </LangButton>
            <Menu
              anchorEl={langAnchorEl}
              open={isLangMenuOpen}
              onClose={handleLangMenuClose}
              TransitionComponent={Fade}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              sx={{ [`&.${popoverClasses.root}`]: { marginTop: '4px' } }}
            >
              <StyledMenuItem onClick={() => handleChangeLang('ko')}>
                <span className={i18n.language === 'ko' ? 'active' : undefined}>{t('Language.Korean')}</span>
                {i18n.language === 'ko' && <CheckIcon width={20} height={20} color={theme.colors['ic-primary-light']} />}
              </StyledMenuItem>
              <StyledMenuItem onClick={() => handleChangeLang('en')}>
                <span className={i18n.language === 'en' ? 'active' : undefined}>{t('Language.English')}</span>
                {i18n.language === 'en' && <CheckIcon width={20} height={20} color={theme.colors['ic-primary-light']} />}
              </StyledMenuItem>
            </Menu>
          </RightSection>
        </>
      )}
    </GnbContainer>
  );
});

const ProviderStoreMobileGnb = () => {
  const { t } = useTranslation();
  const { providerDomain } = useParams<{ providerDomain: string }>();
  const { INTRODUCTION_PAGE, COMPANY_ID } = getEnvConfig();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const providerStorePath = `/stores/${providerDomain}`;

  const gnbItems = useMemo(
    () => [
      ...(INTRODUCTION_PAGE === 'true'
        ? [{ id: 0, name: t('GNB_Menu_01'), to: `${providerStorePath}/introduction` }]
        : []),
      { id: 1, name: t('GNB_Menu_02'), to: providerStorePath },
    ],
    [INTRODUCTION_PAGE, t, providerStorePath],
  );

  return (
    <>
      <LogoLinkWrap to={providerStorePath}>
        <img
          src={COMPANY_ID === 'KT_DS' ? '/logo-ktds.png' : '/logo-mobile.png'}
          alt="Logo"
          style={{ width: '100%', height: '100%', objectFit: 'scale-down' }}
        />
      </LogoLinkWrap>
      {open && (
        <MobileMenuOverlay onClick={() => setOpen(false)}>
          <MobileMenuList onClick={e => e.stopPropagation()}>
            {gnbItems.map(item => (
              <MobileMenuItem
                key={item.id}
                onClick={() => {
                  setOpen(false);
                  navigate(item.to);
                }}
              >
                {item.name}
              </MobileMenuItem>
            ))}
          </MobileMenuList>
        </MobileMenuOverlay>
      )}
    </>
  );
};

const GnbContainer = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--employee-gnb-height);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 50px;
  background-color: #fff;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.09);
  flex-shrink: 0;
  gap: 40px;
  z-index: 1001;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    height: var(--mobile-employee-gnb-height);
    padding: 0 26px 0 24px;
    gap: 15px;
  }
`;

const LogoLinkWrap = styled(Link)`
  width: 148px;
  height: 25px;
  display: inline-block;
  font-weight: 700;
  text-decoration: none;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    width: 139px;
    height: 22px;
    margin-right: auto;
  }
  z-index: 1001;
`;

const MenusWrapper = styled(Box)`
  max-width: 1440px;
  height: 100%;
  display: flex;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
  justify-content: center;
  gap: 32px;
  align-items: center;
`;

const StyledRoutingTabs = styled(RoutingTabs)`
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  & .en-tab-item {
    min-width: auto;
    padding: 0 46px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

const LangButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }
`;

const StyledMenuItem = styled(MenuItem)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 140px;
  padding: 10px 12px;
  ${fonts.Body2};

  & > span {
    color: ${({ theme }) => theme.colors['text-gray-main']};

    &.active {
      color: ${({ theme }) => theme.colors['text-primary-light']};
    }
  }

  &:hover {
    background: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }
`;

const IntroductionBtn = styled(Button)`
  position: relative;
  border: 0;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  border-image-slice: 1;
  z-index: 1;

  span {
    background: ${({ theme }) => theme.colors['bg-gradient']};
    border-image-slice: 1;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
  }

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 2px;
    left: 2px;
    width: calc(100% - 2px * 2);
    height: calc(100% - 2px * 2);
    background: #fff;
    border-radius: calc(16px - 2px);
    z-index: -1;
  }

  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
    box-shadow: none;
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1002;
`;

const MobileMenuList = styled.ul`
  position: absolute;
  top: 0;
  right: 0;
  width: 240px;
  height: 100vh;
  background-color: #fff;
  padding: 60px 0 0;
`;

const MobileMenuItem = styled.li`
  display: flex;
  align-items: center;
  height: 52px;
  padding: 0 24px;
  cursor: pointer;
  ${fonts.Body1};
  color: ${({ theme }) => theme.colors['text-gray-main']};

  &:hover {
    background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
    color: ${({ theme }) => theme.colors['text-primary-light']};
  }
`;

const Main = styled.div`
  width: 100%;
  padding-top: var(--employee-gnb-height);
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    padding-top: var(--mobile-employee-gnb-height);
  }
`;

const ContentsWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
`;
